import { FC, ReactNode, useEffect } from 'react'
import { IconX } from '@tabler/icons';
import { Button } from '../Button'
import { FormattedMessage } from 'react-intl';

interface button {
  label: string | JSX.Element
  type?: string
  action?: string
  disabled?: boolean
  onClick: (...args: any[]) => void
  labelStyles?: string
  className?: string
  id?: string
  dataAttribute?: string
  reactIntlId?: string
  icon?: JSX.Element
}

interface ModalBodyProps {
  buttons: button[]
  cancelButton?: boolean
  cancelButtonIntlId?: string
  cancelButtonText?: string | JSX.Element
  title: string | JSX.Element | ReactNode | undefined
  action: "default" | "primary" | "success" | "warning" | "danger"
  icon?: JSX.Element
  closeIcon?: boolean
  description?: string | ReactNode,
  onClose?: () => void
  handleClose?: () => void
  closeOnSubmit?: boolean
  handleEnter?: () => void
  footer?: boolean,
  footerBody?: Element,
  cn?: string
  isCustomBody?: boolean,
  closeIconDataAttribute?: string,
  reactIntlTitleId?: string,
  reactIntlDescriptionId?: string,
  reactIntlValues?: any,
  leftSideCta?: button,
  showLeftSideCta?: boolean,
  showDescriptionChildren?: boolean,
  iconInline?: boolean,
  footerMargin?: string
}

const utilityTypes = {
  default: 'bg-gray-400 text-primary-light',
  primary: 'bg-primary-lighter text-primary-dark',
  success: 'text-success-dark bg-success-lighter',
  warning: 'bg-warning-light text-warning-dark',
  danger: 'bg-danger-light text-danger-dark',
}


export const ModalBody: FC<ModalBodyProps> = ({ 
  cn = 'pt-[30px]',
  buttons, 
  cancelButton, 
  cancelButtonText = "Cancel",
  title,
  isCustomBody = false,
  closeIcon = true,
  icon,
  action = "success",
  children,
  description,
  handleClose,
  cancelButtonIntlId="global.modal.cancel",
  closeOnSubmit,
  handleEnter,
  footer = true,
  footerBody,
  closeIconDataAttribute,
  reactIntlTitleId,
  reactIntlDescriptionId,
  reactIntlValues,
  leftSideCta,
  showLeftSideCta = false,
  showDescriptionChildren = false,
  iconInline = false,
  footerMargin = 'mt-[42px]'
}) => {

  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      if ((event.code === "Enter" || event.code === "NumpadEnter") && handleEnter) {
          event.preventDefault();
          handleEnter()
        }
      };
      
      document.addEventListener("keydown", listener);
      
      return () => document.removeEventListener("keydown", listener)
  }, [handleEnter]);

  return <div className={`flex flex-1 flex-col ${cn}`}>
          <div className="flex justify-between mx-[30px] relative">
            <div className={iconInline ? 'flex items-center gap-2' : 'flex flex-col'}>
              {
              icon && <div className={`${utilityTypes[action]} text-lg font-medium w-[40px] h-[40px] border border-white shadow-sm flex items-center justify-center rounded-full shrink-0`}>
                {icon}
              </div>
              }
              <h1 className={`${icon && !iconInline && 'ml-[4px] mt-[8px]'} text-secondary max-w-[360px] overflow-ellipsis font-medium overflow-hidden whitespace-nowrap leading-[17.78px] text-[15px]`}>
                { (reactIntlTitleId && typeof title === 'string') ? <FormattedMessage id={reactIntlTitleId} defaultMessage={title} /> : title }
              </h1>
              {
                closeIcon && 
                <IconX 
                  className="cursor-pointer absolute right-[-2px] top-[24%] translate-y-[-50%] text-secondary" 
                  size="20px" 
                  stroke="1.25" 
                  data-attribute = "icon-close"
                  onClick={() => handleClose && handleClose()}
                  {...(closeIconDataAttribute && {"data-attribute": `modal-close-${closeIconDataAttribute}`})}
                />
              }
            </div>
          </div>

          {showDescriptionChildren ? (
            <>
              {description && (
                <div className={`${icon ? 'px-[34px] mt-[8px]' : 'px-[30px] mt-[24px]'} text-lg text-gray-900`}>
                  {(reactIntlDescriptionId && typeof description === 'string') 
                    ? <FormattedMessage id={reactIntlDescriptionId} defaultMessage={description} values={reactIntlValues}/> 
                    : description}
                </div>
              )}
              {children && (
                <div className={`${icon ? 'px-[34px] mt-[8px]' : `${!isCustomBody && 'px-[30px]'} ${title && 'mt-[24px]'}`}`}>
                  {children}
                </div>
              )}
            </>
          ) : (
            <>
              {!description && children && (
                <div className={`${icon ? 'px-[34px] mt-[8px]' : `${!isCustomBody && 'px-[30px]'} ${title && 'mt-[24px]'}`}`}>
                  {children}
                </div>
              )}
              {description && !children && (
                <div className={`${icon ? 'px-[34px] mt-[8px]' : 'px-[30px] mt-[24px]'} text-lg text-gray-900`}>
                  {(reactIntlDescriptionId && typeof description === 'string') 
                    ? <FormattedMessage id={reactIntlDescriptionId} defaultMessage={description} values={reactIntlValues}/> 
                    : description}
                </div>
              )}
            </>
          )}
     

      { 
        footer && 
        <div style={{boxShadow:"0px -8px 16px rgba(0,0,0,0.04)"}} className={`rounded-[12px] ${footerMargin} flex items-center justify-start flex-row-reverse px-[24px] py-[16px]`}>
          {
            buttons && buttons?.length > 0 &&  buttons?.map(({ type, label, action, disabled, onClick, labelStyles = 'font-medium uppercase', className, id, dataAttribute, reactIntlId, icon }, index) =>
            <Button
              key={index}
              disabled={disabled}
              label={label}
              type={type}
              action={action}
              labelStyles={labelStyles}
              className={className}
              id={id}
              reactIntlId={reactIntlId}
              icon={icon}
              onClick={async () => {
                onClick && await onClick()
                if(closeOnSubmit){
                  handleClose && handleClose()
                }
              }}
              {...(dataAttribute && { dataAttribute })}
              />
              )}
            {cancelButton && <Button className="mr-[8px]" dataAttribute={"Cancel"} labelStyles="font-medium text-secondary opacity-60 uppercase" label={cancelButtonText} type="link" action="default" onClick={() => handleClose && handleClose()}
              />}
            {
              showLeftSideCta && 
              <div className="flex w-full items-center justify-start">
                <Button 
                  disabled={leftSideCta?.disabled}
                  label={leftSideCta?.label} 
                  dataAttribute={leftSideCta?.dataAttribute}
                  type={leftSideCta?.type} 
                  action={leftSideCta?.action} 
                  labelStyles={leftSideCta?.labelStyles}
                  className={leftSideCta?.className}
                  id={leftSideCta?.id}
                  reactIntlId={leftSideCta?.reactIntlId}
                  icon={leftSideCta?.icon}
                  onClick={async () => {
                    leftSideCta?.onClick && await leftSideCta?.onClick()
                  }}
                />
              </div>
            }
        </div>
      }
      {
        footerBody && footerBody
      }
      </div>
}


ModalBody.defaultProps = {
  cancelButton: true,
  cancelButtonText: "Cancel",
  cancelButtonIntlId: "global.modal.cancel",
  title: "Create New Modal",
  action: "primary",
  closeIcon: true,
  closeOnSubmit: true,
  footer: true
}